import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IApiServiceError } from "store";
import { IClientFilters, IClient, CreateClientPayload } from "store/types/user-management";
import { IPaginationResponse } from "store/types";
import { useClientQueries } from "../query-context";
import type { IEntity } from "components/User-management/Clients/EntitiesTab.types";

export interface UseClientsListParams {
  filters: IClientFilters;
  enabled?: boolean;
  keepPreviousData?: boolean;
}

/**
 * React Query hook for fetching the client list from the API.
 *
 * Query key structure: `["clients", search, type, rm]`
 * The key adapts to local filter states so that any filter change triggers a refetch.
 */
export const useClientsList = (params: UseClientsListParams): UseQueryResult<
  IPaginationResponse<IClient>,
  IApiServiceError
> & {
  refetchClients: () => void;
  isFetching: boolean;
} => {
  const { getClientsList } = useClientQueries();
  const { filters, enabled = true, keepPreviousData = true } = params;

  const queryResult = useQuery<IPaginationResponse<IClient>, IApiServiceError>(
    ["clients", filters.search, filters.type, filters.rm],
    () => getClientsList(filters),
    {
      enabled: enabled,
      retry: (_: number, error: IApiServiceError) => {
        // Don't retry on validation or client errors (4xx)
        if (error?.status || error?.response?.status) {
          return false;
        }
        return false;
      },
      keepPreviousData,
      placeholderData: (previous: IPaginationResponse<IClient> | undefined) => previous // Keep previous data during refetch
    },
  );

  const refetchClients = async () => {
    await queryResult.refetch();
  };

  return {
    ...queryResult,
    refetchClients
  };
};

export interface UseCreateClientParams {
  onMutationSuccess?: () => void;
}

/**
 * React Query mutation hook for creating a new client.
 * Invalidates the clients list cache on success.
 */
export const useCreateClientMutation = (params?: UseCreateClientParams) => {
  const queryClient = useQueryClient();
  const { createClient } = useClientQueries();

  return useMutation<number, IApiServiceError, CreateClientPayload>(
    (payload) => createClient(payload.name, payload.type, payload.rm, payload.country, payload.primaryCurrency),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["clients"] });
        params?.onMutationSuccess?.();
      },
    },
  );
};

export interface UseRMListResult {
  rms: string[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * React Query hook for fetching the distinct RM list from the backend.
 * Used to populate the RM filter dropdown in the Clients panel.
 */
export const useRMList = (): UseRMListResult => {
  const { getRMList } = useClientQueries();

  const queryResult = useQuery<string[], IApiServiceError>(
    ["clients-rm-list"],
    () => getRMList(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes — RM list changes infrequently
      placeholderData: [],
    },
  );

  return {
    rms: queryResult.data ?? [],
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    refetch: queryResult.refetch,
  };
};

// ---------------------------------------------------------------------------
// React Query hook for fetching a single client's detail.
// ---------------------------------------------------------------------------
export const useClientDetail = (clientId: number, enabled: boolean = true) => {
  const { getClientDetail } = useClientQueries();

  return useQuery<
    {
      id: number;
      name: string;
      type: string | null;
      rm: string | null;
      domicile: string | null;
      primaryCurrency: string | null;
      contact: string | null;
      entities: IEntity[];
    },
    IApiServiceError
  >(
    ["client-detail", clientId],
    () => getClientDetail(clientId),
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      retry: (_: number, error: IApiServiceError) => {
        if (error?.status || error?.response?.status) return false;
        return false;
      },
    },
  );
};
